import {
  IRegistrationGuest,
  MAX_REGISTRATION_GUESTS,
} from "../interfaces/models/IRegistration";

type GuestSource = {
  guests?: Array<Partial<IRegistrationGuest> | null | undefined> | null;
  guestName?: string | null;
  guestName2?: string | null;
  registrationDate?: number | null;
};

function readUnixSeconds(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function guestContentKey(
  guest: Pick<IRegistrationGuest, "name" | "specialRequest" | "registrationDate">,
): string {
  return `${guest.name.trim()}|${(guest.specialRequest ?? "").trim()}|${guest.registrationDate}`;
}

function isValidGuest(value: unknown): value is IRegistrationGuest {
  if (!value || typeof value !== "object") {
    return false;
  }
  const guest = value as IRegistrationGuest;
  return (
    typeof guest.id === "string" &&
    guest.id.trim().length > 0 &&
    typeof guest.name === "string" &&
    guest.name.trim().length > 0 &&
    typeof guest.registrationDate === "number" &&
    Number.isFinite(guest.registrationDate)
  );
}

/**
 * Prefer `guests[]`; fall back to legacy `guestName` / `guestName2`.
 * Always returns at most MAX_REGISTRATION_GUESTS entries with trimmed names.
 */
export function normalizeRegistrationGuests(
  reg: GuestSource | null | undefined,
): IRegistrationGuest[] {
  if (!reg) {
    return [];
  }

  const rootDate =
    typeof reg.registrationDate === "number" && Number.isFinite(reg.registrationDate)
      ? reg.registrationDate
      : Math.floor(Date.now() / 1000);

  if (Array.isArray(reg.guests) && reg.guests.length > 0) {
    return reg.guests
      .filter(isValidGuest)
      .map((guest) => ({
        id: guest.id.trim(),
        name: guest.name.trim(),
        specialRequest:
          typeof guest.specialRequest === "string" && guest.specialRequest.trim()
            ? guest.specialRequest.trim()
            : undefined,
        registrationDate: guest.registrationDate,
        modificationDate: readUnixSeconds(
          guest.modificationDate,
          guest.registrationDate,
        ),
      }))
      .slice(0, MAX_REGISTRATION_GUESTS);
  }

  const legacy: IRegistrationGuest[] = [];
  const g1 = typeof reg.guestName === "string" ? reg.guestName.trim() : "";
  const g2 = typeof reg.guestName2 === "string" ? reg.guestName2.trim() : "";
  if (g1) {
    legacy.push({
      id: "guest-1",
      name: g1,
      registrationDate: rootDate,
      modificationDate: rootDate,
    });
  }
  if (g2) {
    legacy.push({
      id: "guest-2",
      name: g2,
      registrationDate: rootDate,
      modificationDate: rootDate,
    });
  }
  return legacy.slice(0, MAX_REGISTRATION_GUESTS);
}

export function countRegistrationGuestsFromModel(
  reg: GuestSource | null | undefined,
): number {
  return normalizeRegistrationGuests(reg).length;
}

export function createEmptyRegistrationGuest(
  registrationDate?: number,
): IRegistrationGuest {
  const now = Math.floor(Date.now() / 1000);
  return {
    id: crypto.randomUUID(),
    name: "",
    specialRequest: "",
    registrationDate:
      typeof registrationDate === "number" && Number.isFinite(registrationDate)
        ? registrationDate
        : now,
    modificationDate: now,
  };
}

/**
 * Keeps `registrationDate` as the creation timestamp and refreshes
 * `modificationDate` when the guest is new or its saved fields changed.
 */
export function applyGuestModificationDates(
  next: IRegistrationGuest[],
  previous: IRegistrationGuest[] | null | undefined,
  now = Math.floor(Date.now() / 1000),
): IRegistrationGuest[] {
  const previousById = new Map(
    (previous ?? []).filter((guest) => guest?.id).map((guest) => [guest.id, guest]),
  );

  return next.map((guest) => {
    const prior = previousById.get(guest.id);
    if (!prior || guestContentKey(prior) !== guestContentKey(guest)) {
      return { ...guest, modificationDate: now };
    }
    return {
      ...guest,
      registrationDate: prior.registrationDate,
      modificationDate: readUnixSeconds(
        prior.modificationDate,
        prior.registrationDate,
      ),
    };
  });
}

export function sanitizeGuestsForPersist(
  guests: unknown,
  fallbackRegistrationDate?: number,
): IRegistrationGuest[] {
  const rootDate =
    typeof fallbackRegistrationDate === "number" &&
    Number.isFinite(fallbackRegistrationDate)
      ? fallbackRegistrationDate
      : Math.floor(Date.now() / 1000);

  if (!Array.isArray(guests)) {
    return [];
  }

  const sanitized: IRegistrationGuest[] = [];
  for (const raw of guests) {
    if (sanitized.length >= MAX_REGISTRATION_GUESTS) {
      break;
    }
    if (!raw || typeof raw !== "object") {
      continue;
    }
    const guest = raw as Partial<IRegistrationGuest>;
    const name = typeof guest.name === "string" ? guest.name.trim() : "";
    if (!name) {
      continue;
    }
    const id =
      typeof guest.id === "string" && guest.id.trim()
        ? guest.id.trim()
        : crypto.randomUUID();
    const registrationDate =
      typeof guest.registrationDate === "number" &&
      Number.isFinite(guest.registrationDate)
        ? guest.registrationDate
        : rootDate;
    const specialRequest =
      typeof guest.specialRequest === "string" && guest.specialRequest.trim()
        ? guest.specialRequest.trim()
        : undefined;
    const modificationDate = readUnixSeconds(
      guest.modificationDate,
      registrationDate,
    );
    sanitized.push({ id, name, specialRequest, registrationDate, modificationDate });
  }
  return sanitized;
}

/**
 * Saved guests stay on the registration. Delete only undoes an Add Guest
 * that has not been written yet.
 */
export function canRemoveUnsavedGuest(
  guestId: string,
  persistedGuests: ReadonlyArray<{ id: string }> | null | undefined,
): boolean {
  if (!guestId) {
    return false;
  }
  return !(persistedGuests ?? []).some((guest) => guest.id === guestId);
}

/** True when a guest row is shown but name is empty (must fill or remove before save). */
export function hasVisibleGuestWithMissingName (
  guests: IRegistrationGuest[] | null | undefined,
): boolean {
  if (!Array.isArray(guests) || guests.length === 0) {
    return false;
  }
  return guests.some(
    (guest) => !(typeof guest?.name === "string" && guest.name.trim()),
  );
}
